import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Chip, FormSection } from "@/components/common/Chips";
import { UgcTermsDialog } from "@/components/activity/UgcTermsDialog";
import { useProfile } from "@/context/ProfileContext";
import { useLanguage } from "@/i18n/LanguageContext";
import {
  ACTIVITY_TYPES,
  DURATION_PRESETS,
  INTENSITIES,
  MOODS,
  kebab,
} from "@/lib/constants";

export const TypeSection = ({ value, onChange, delay }) => {
  const { t } = useLanguage();
  return (
    <FormSection title={t("addActivity.typeSection")} delay={delay}>
      <div data-testid="add-activity-type" className="flex flex-wrap gap-2">
        {ACTIVITY_TYPES.map(({ id, icon: Icon }) => (
          <Chip
            key={id}
            testId={`add-activity-type-${kebab(id)}`}
            selected={value === id}
            onClick={() => onChange(id)}
            className="flex items-center gap-1.5"
          >
            <Icon size={15} />
            {t(`types.${id}`)}
          </Chip>
        ))}
      </div>
    </FormSection>
  );
};

export const DurationSection = ({
  duration,
  customMode,
  customValue,
  onPreset,
  onCustomMode,
  onCustomValue,
  delay,
}) => {
  const { t } = useLanguage();
  return (
    <FormSection title={t("addActivity.durationSection")} delay={delay}>
      <div data-testid="add-activity-duration" className="flex flex-wrap gap-2">
        {DURATION_PRESETS.map((d) => (
          <Chip
            key={d}
            testId={`add-activity-duration-preset-${d}`}
            selected={!customMode && duration === d}
            onClick={() => onPreset(d)}
          >
            {d}
          </Chip>
        ))}
        <Chip
          testId="add-activity-duration-custom"
          selected={customMode}
          onClick={onCustomMode}
        >
          {t("addActivity.custom")}
        </Chip>
      </div>
      {customMode && (
        <Input
          data-testid="add-activity-duration-custom-input"
          type="number"
          min={1}
          max={720}
          value={customValue}
          onChange={(e) => onCustomValue(e.target.value)}
          placeholder={t("addActivity.customPlaceholder")}
          className="mt-3 h-11 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/35 focus-visible:ring-2 focus-visible:ring-cyan-300/60"
        />
      )}
    </FormSection>
  );
};

export const IntensitySection = ({ value, onChange, delay }) => {
  const { t } = useLanguage();
  return (
    <FormSection title={t("addActivity.intensitySection")} delay={delay}>
      <div data-testid="add-activity-intensity" className="flex gap-2">
        {INTENSITIES.map((i) => (
          <Chip
            key={i}
            testId={`add-activity-intensity-${kebab(i)}`}
            selected={value === i}
            onClick={() => onChange(i)}
            className="flex-1 text-center justify-center flex"
          >
            {t(`intensities.${i}`)}
          </Chip>
        ))}
      </div>
    </FormSection>
  );
};

export const MoodSection = ({ title, testPrefix, value, onChange, delay }) => {
  const { t } = useLanguage();
  return (
    <FormSection title={title} delay={delay}>
      <div data-testid={testPrefix} className="flex flex-wrap gap-2">
        {MOODS.map((m) => (
          <Chip
            key={m}
            testId={`${testPrefix}-${kebab(m)}`}
            selected={value === m}
            onClick={() => onChange(value === m ? null : m)}
          >
            {t(`moods.${m}`)}
          </Chip>
        ))}
      </div>
    </FormSection>
  );
};

export const NoteSection = ({ value, onChange, delay }) => {
  const { t } = useLanguage();
  return (
    <FormSection title={t("addActivity.noteSection")} delay={delay}>
      <Textarea
        data-testid="add-activity-note-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t("addActivity.notePlaceholder")}
        maxLength={280}
        rows={3}
        className="rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/35 focus-visible:ring-2 focus-visible:ring-cyan-300/60 resize-none"
      />
    </FormSection>
  );
};

// Stage 2.1: optional share-to-community toggle (privacy-aware default)
// Phase 10 (UGC): turning sharing ON for the first time requires explicit
// acceptance of the community Terms (dialog gate; server also enforces it).
export const ShareSection = ({ value, onChange, isPrivate, delay }) => {
  const { t } = useLanguage();
  const { profile } = useProfile();
  const [termsOpen, setTermsOpen] = useState(false);

  const handleToggle = (checked) => {
    if (checked && !profile?.ugc_terms_accepted_at) {
      setTermsOpen(true); // keep toggle OFF until terms are accepted
      return;
    }
    onChange(checked);
  };

  return (
    <FormSection title={t("addActivity.shareSection")} delay={delay}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">{t("addActivity.shareLabel")}</p>
          <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">
            {isPrivate ? t("addActivity.shareHintPrivate") : t("addActivity.shareHintPublic")}
          </p>
        </div>
        <Switch
          data-testid="add-activity-share-toggle"
          checked={value}
          onCheckedChange={handleToggle}
        />
      </div>
      <UgcTermsDialog
        open={termsOpen}
        onOpenChange={setTermsOpen}
        onAccepted={() => onChange(true)}
      />
    </FormSection>
  );
};
